import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DEMO_MEDIA_LIMIT, DEMO_MESSAGE_LIMIT, DEMO_TTL_MINUTES } from '@wallflower/shared';
import type { HealthResponse, InstancePricing } from '@wallflower/shared';
import { api, ApiError } from '../lib/api.ts';
import { useAccount, useCapabilities } from '../lib/accountApi.ts';
import { useDocumentTitle } from '../lib/useDocumentTitle.ts';

export default function Landing() {
  const navigate = useNavigate();
  const { account } = useAccount();
  const capabilities = useCapabilities();
  useDocumentTitle('');
  // Starting a demo is the only thing this page still does for itself; creating a
  // real wall belongs to the dashboard now.
  const [demoError, setDemoError] = useState<string | null>(null);
  const [demoBusy, setDemoBusy] = useState(false);

  async function startDemo() {
    setDemoBusy(true);
    setDemoError(null);
    try {
      navigate('/created', { state: await api.createDemoSession(), replace: true });
    } catch (err) {
      setDemoError(describeDemoError(err));
    } finally {
      setDemoBusy(false);
    }
  }

  return (
    // No `justify-center` any more: with the pricing section the page is taller
    // than the viewport, and centring an overflowing flex column clips its top.
    <main className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-6 pt-8 pb-16">
      <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_1fr]">
        <div style={{ animation: 'wf-rise 500ms ease-out both' }}>
          <p className="eyebrow">A slideshow that fills itself</p>
          <h1 className="font-display mt-4 text-5xl leading-[0.95] font-semibold tracking-tight sm:text-6xl">
            Put the screen
            <br />
            in <span className="text-petal">everyone's</span> hands.
          </h1>
          <p className="mt-6 max-w-xl text-lg text-muted">
            Wallfeed puts your event on the big screen and a QR code in the corner. Guests scan,
            send a photo, and watch it appear moments later. You keep a quiet hand on what shows.
          </p>
        </div>
        <ScreenPreview />
      </div>

      <div
        className="panel mt-12 grid gap-5 p-6 sm:p-8"
        style={{ animation: 'wf-rise 600ms 120ms ease-out both' }}
      >
        {/*
          A wall is bought and lasts months, so making one is no longer the sort
          of thing to fire off from a text box on the front page. Signed in, this
          points at the dashboard, where a wall sits among the others and next to
          what it costs. Signed out, it is the pitch.
        */}
        {account ? (
          <div className="grid gap-3">
            <p className="eyebrow">Your events</p>
            <p className="text-muted">
              Your walls live on the dashboard - start a new one there, or pick up the one you
              are already running.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/dashboard" className="btn btn-primary">
                Go to the dashboard
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid gap-3">
            <p className="eyebrow">Run your own</p>
            <p className="text-muted">
              Create an account and your walls stay together in one place, ready for the next
              event.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/signup" className="btn btn-primary">
                Create an account
              </Link>
              <Link to="/signin" className="btn btn-ghost">
                Sign in
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* The demo's button lives in the pricing card below: it is the free tier,
          so it belongs beside the paid one rather than trailing the sign-up. */}
      <Pricing
        capabilities={capabilities}
        onStartDemo={() => void startDemo()}
        demoBusy={demoBusy}
        demoError={demoError}
      />

      <ul className="mt-10 grid gap-6 text-sm text-muted sm:grid-cols-3">
        <li>
          <span className="text-chalk">Nothing to install.</span> Guests use their phone camera and
          a browser.
        </li>
        <li>
          <span className="text-chalk">You stay in control.</span> Hide anything from the screen in
          one tap.
        </li>
        <li>
          <span className="text-chalk">Photos and short clips.</span> Turn video off if you'd
          rather keep it still.
        </li>
      </ul>
    </main>
  );
}

/**
 * What Wallfeed costs, said out loud on the page rather than first met at the
 * checkout. The numbers come from the server because an operator can move them,
 * so hardcoding them here would be a promise this instance might not keep.
 */
function Pricing({
  capabilities,
  onStartDemo,
  demoBusy,
  demoError,
}: {
  capabilities: HealthResponse | null;
  onStartDemo: () => void;
  demoBusy: boolean;
  demoError: string | null;
}) {
  // Three states, and the middle one used to be invisible: still asking, got an
  // answer, and got an answer with no price in it. A server older than this page
  // sends no `pricing` key at all, so check the value rather than trusting the
  // declared shape - a stale dev server is the common way to meet this.
  const asking = capabilities === null;
  const pricing = capabilities?.pricing ?? null;
  const price = pricing ? formatPrice(pricing) : null;
  const days = pricing?.ttlDays ?? 0;

  return (
    <section
      className="mt-12 grid gap-5"
      style={{ animation: 'wf-rise 650ms 170ms ease-out both' }}
      aria-labelledby="pricing-heading"
    >
      <h2 id="pricing-heading" className="eyebrow">
        Pricing
      </h2>

      <div className="grid gap-5 sm:grid-cols-2">
        <article className="panel grid gap-4 p-6">
          <div>
            <p className="eyebrow text-petal">The full wall</p>
            {/*
              Held back until the server has answered rather than rendered empty
              and filled in: a price that appears a beat late reads as loading, a
              price that changes under the reader's eye reads as a trick. But a
              blank that never fills reads as broken, so say so once it is.
            */}
            {price ? (
              <p className="font-display mt-2 text-4xl font-semibold tracking-tight">{price}</p>
            ) : asking ? (
              <p className="font-display mt-2 text-4xl font-semibold tracking-tight">
                <span className="text-muted">&nbsp;</span>
              </p>
            ) : (
              <p className="mt-2 text-sm text-muted" role="status">
                We cannot reach the till right now, so the price is not shown. Try again in a
                moment, or start a free demo wall alongside in the meantime.
              </p>
            )}
            {/* The day count used to be repeated here; the feature list below
                says it once, and once is enough. */}
            <p className="mt-1 text-sm text-muted">
              One payment, one event. No subscription.
            </p>
          </div>

          <ul className="grid gap-2 text-sm text-muted">
            {pricing && pricing.mediaLimit > 0 ? (
              <li>
                <span className="text-chalk">Up to {pricing.mediaLimit.toLocaleString()}</span>{' '}
                photos and videos.
              </li>
            ) : null}
            <li>
              <span className="text-chalk">Unlimited messages</span> in the ticker.
            </li>
            {days > 0 ? (
              <li>
                <span className="text-chalk">Stays up for {days} days</span> from the day you
                pay for it.
              </li>
            ) : null}
            <li>
              <span className="text-chalk">As many guests as turn up.</span> No per-person charge.
            </li>
          </ul>
        </article>

        {/* `content-between` so the button sits on the card's floor rather than
            floating wherever the shorter list leaves it. */}
        <article className="panel grid content-between gap-4 p-6">
          <div>
            <p className="eyebrow">Try it first</p>
            <p className="font-display mt-2 text-4xl font-semibold tracking-tight">Free</p>
            <p className="mt-1 text-sm text-muted">
              No account, no card. The whole thing, on a short lead.
            </p>
          </div>

          <ul className="grid gap-2 text-sm text-muted">
            <li>
              <span className="text-chalk">{DEMO_TTL_MINUTES} minutes</span> from the moment you
              start it.
            </li>
            <li>
              <span className="text-chalk">
                {DEMO_MEDIA_LIMIT} photos and {DEMO_MESSAGE_LIMIT} messages.
              </span>{' '}
              Photos only, no video.
            </li>
          </ul>

          <div className="grid gap-3">
            {demoError ? (
              <p role="alert" className="text-sm text-danger">
                {demoError}
              </p>
            ) : null}
            {/* Ghost, not primary: creating an account is still the page's main
                road, and two primary buttons would argue about which is. */}
            <button
              type="button"
              className="btn btn-ghost justify-self-start"
              onClick={onStartDemo}
              disabled={demoBusy}
            >
              {demoBusy ? 'Setting it up…' : 'Try a demo wall'}
            </button>
          </div>
        </article>
      </div>

      {/*
        The part a host would be angriest to find out late. Cleanup deletes the
        wall and its files at expiry with no grace period, so it belongs on the
        way in, not in a support email on the day it happens.
      */}
      {/* Never gated on the term loading: not knowing how long a wall lasts is no
          reason to stop telling someone it gets deleted at the end of it. */}
      <p className="text-sm text-muted">
        <span className="text-chalk">Keep what you want to keep.</span>{' '}
        {days > 0 ? `After ${days} days the` : "When a wall's time is up, the"} wall and every
        photo, video and message on it are deleted for good. Download anything you want before
        then - there is no archive to ask us for afterwards.
      </p>
    </section>
  );
}

/**
 * Stripe reports minor units, and how many of those make a unit is currency
 * business - JPY has none, so dividing by 100 would render the price a hundred
 * times small. Ask Intl what this currency does instead of assuming.
 */
function formatPrice({ amount, currency }: InstancePricing): string | null {
  if (amount === null || !currency) return null;
  try {
    const format = new Intl.NumberFormat(undefined, { style: 'currency', currency });
    const exponent = format.resolvedOptions().maximumFractionDigits ?? 2;
    return format.format(amount / 10 ** exponent);
  } catch {
    // An unknown currency code should cost us the figure, not the page.
    return null;
  }
}

/** The demo needs no account, so the only interesting failure is the per-IP cap. */
function describeDemoError(err: unknown): string {
  if (err instanceof ApiError && err.status === 429) {
    return 'A lot of demo walls have been started from this network just now. Try again a little later.';
  }
  return err instanceof Error ? err.message : 'Could not start a demo wall.';
}

/**
 * The hero is the product: a screen on a wall with the place card resting in
 * the corner, which is the one thing a guest ever sees of Wallfeed.
 */
function ScreenPreview() {
  return (
    <div
      className="relative aspect-video w-full overflow-hidden rounded-xl border border-edge"
      style={{
        background: 'radial-gradient(120% 100% at 30% 20%, #4a3350 0%, #241a2c 55%, #14101c 100%)',
        boxShadow: '0 30px 70px -30px rgba(0,0,0,0.9)',
        animation: 'wf-rise 700ms 220ms ease-out both',
      }}
      aria-hidden
    >
      {/*
        A stand-in for a guest's photo, framed as the slideshow would frame it.
        Mirrored so the place card in the right corner lands on the table rather
        than on someone's face.
      */}
      <img
        src="/hero-example.jpg"
        alt=""
        className="absolute inset-0 h-full w-full rounded-[inherit] object-cover"
        draggable={false}
      />
      <div
        className="absolute inset-0"
        style={{ boxShadow: 'inset 0 0 120px 40px rgba(10,7,15,0.75)' }}
      />

      <span
        className="absolute bottom-[8%] left-[5%] rounded-full px-3 py-1 text-xs text-chalk/90 backdrop-blur-md"
        style={{ background: 'rgba(16,13,23,0.55)', fontFamily: 'var(--font-display)' }}
      >
        Callum
      </span>

      <div className="place-card absolute right-[6%] bottom-[8%] w-[26%] -rotate-[1.2deg] p-[4%]">
        <div className="grid gap-px" style={{ gridTemplateColumns: 'repeat(13, 1fr)' }}>
          {QR_SKETCH.map((filled, index) => (
            <span
              key={index}
              className="aspect-square"
              style={{ background: filled ? '#221c17' : 'transparent' }}
            />
          ))}
        </div>
        <p
          className="mt-[7%] text-center text-[0.6rem] leading-tight font-semibold"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          Scan to add your photos!
        </p>
      </div>
    </div>
  );
}

/**
 * A 13x13 sketch of a QR code: three finder squares and a deterministic fill.
 * Suggestive of the real thing, deliberately not scannable.
 */
const QR_SKETCH = (() => {
  const size = 13;
  const cells: boolean[] = [];
  const inFinder = (row: number, col: number) => {
    const corner =
      (row < 7 && col < 7) || (row < 7 && col >= size - 7) || (row >= size - 7 && col < 7);
    if (!corner) return null;
    const r = row < 7 ? row : row - (size - 7);
    const c = col < 7 ? col : col - (size - 7);
    const ring = Math.max(Math.abs(r - 3), Math.abs(c - 3));
    return ring === 3 || ring <= 1;
  };

  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) {
      const finder = inFinder(row, col);
      // A cheap hash keeps the pattern stable between renders.
      cells.push(finder ?? ((row * 7 + col * 13 + ((row * col) % 5)) % 3 === 0));
    }
  }
  return cells;
})();
